import React, {
  useMemo,
  useCallback,
  useTransition,
  useEffect,
  memo
} from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useParams, useNavigate } from 'react-router-dom';
import {
  CreateArrayOptions,
  NUMERIC_ACTIVE_INACTIVE_OPTIONS,
  ROUTE_PATHS
} from '@/constants';
import {
  useCreateNewStaffMutation,
  useEditStaffMutation,
  useGetStaffDetailsQuery
} from '@/services/staff.service';
import toast from 'react-hot-toast';
import { useGetPrefectureListQuery } from '@/services';
import { FormSelect } from '../Common/FormFields';

interface Prefecture {
  name: string;
}

interface PrefectureListResponse {
  items: Prefecture[];
}
// Define the form data structure based on the required payload
const formatMonthValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const staffManagerSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  dob: z.string().min(1, 'Date of birth is required'),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(1, 'Phone is required'),
  address_line_1: z.string().min(1, 'Address line 1 is required'),
  address_line_2: z.string().min(1, 'Address line 2 is required'),
  city: z.string().min(1, 'City/Town is required'),
  prefecture: z.string().min(1, 'Prefecture is required'),
  zip_code: z.string().min(1, 'Zip code is required'),
  my_number: z.string().min(1, 'My number is required'),
  status: z.number(),
  pay_period: z.string().min(1, 'Pay period is required'),
  pay_period_month: z
    .string()
    .min(1, 'Pay month is required')
    .regex(/^\d{4}-\d{2}$/, 'Pay month must be in YYYY-MM format'),
  payment_date: z
    .number()
    .min(1, 'Payment date must be at least 1')
    .max(31, 'Payment date must not exceed 31'),
  monthly_pay_rate: z
    .string()
    .min(1, 'Monthly pay rate is required')
    .regex(/^\d+(\.\d+)?$/, 'Monthly pay rate must be 0 or greater'),
  federal_tax: z
    .string()
    .min(1, 'Federal tax is required')
    .regex(/^\d+(\.\d+)?$/, 'Federal tax must be 0 or greater'),
  health_insurance: z
    .string()
    .min(1, 'Health insurance is required')
    .regex(/^\d+(\.\d+)?$/, 'Health insurance must be 0 or greater'),
  pension: z
    .string()
    .min(1, 'Pension is required')
    .regex(/^\d+(\.\d+)?$/, 'Pension must be 0 or greater'),
  insurance: z
    .string()
    .min(1, 'Insurance is required')
    .regex(/^\d+(\.\d+)?$/, 'Insurance must be 0 or greater')
});

type StaffManagerFormData = z.infer<typeof staffManagerSchema>;

// Pay period options
const PAY_PERIOD_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi-weekly', label: 'Bi-Weekly' }
];

// Custom hook for form management
const useStaffForm = (id?: string, staffDetails?: unknown) => {
  const defaultValues = useMemo<Partial<StaffManagerFormData>>(
    () => ({
      name: '',
      dob: '',
      email: '',
      phone: '',
      address_line_1: '',
      address_line_2: '',
      city: '',
      prefecture: '',
      zip_code: '',
      my_number: '',
      status: 1,
      pay_period: '',
      pay_period_month: formatMonthValue(new Date()),
      payment_date: 1,
      monthly_pay_rate: '',
      federal_tax: '',
      health_insurance: '',
      pension: '',
      insurance: ''
    }),
    []
  );

  const form = useForm<StaffManagerFormData>({
    resolver: zodResolver(staffManagerSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues
  });

  // Set form values when staffDetails are loaded
  useEffect(() => {
    if (id && staffDetails) {
      const staff = staffDetails as Partial<StaffManagerFormData>;
      form.reset({
        name: staff.name || '',
        dob: staff.dob || '',
        email: staff.email || '',
        phone: staff.phone || '',
        address_line_1: staff.address_line_1 || '',
        address_line_2: staff.address_line_2 || '',
        city: staff.city || '',
        prefecture: staff.prefecture || '',
        zip_code: staff.zip_code || '',
        my_number: staff.my_number || '',
        status: staff.status ?? 1,
        pay_period: staff.pay_period || '',
        pay_period_month: (staff as any).pay_period_month
          ? String((staff as any).pay_period_month).slice(0, 7)
          : formatMonthValue(new Date()),
        payment_date: staff.payment_date || 1,
        monthly_pay_rate: staff.monthly_pay_rate
          ? String(staff.monthly_pay_rate)
          : '',
        federal_tax: staff.federal_tax ? String(staff.federal_tax) : '',
        health_insurance: staff.health_insurance
          ? String(staff.health_insurance)
          : '',
        pension: staff.pension ? String(staff.pension) : '',
        insurance: staff.insurance ? String(staff.insurance) : ''
      });
    } else if (!id) {
      form.reset(defaultValues);
    }
  }, [id, staffDetails, form, defaultValues]);

  return form;
};

// Memoized form field component
const FormField = memo(
  ({
    label,
    required = false,
    error,
    children
  }: {
    label: string;
    required?: boolean;
    error?: string;
    children: React.ReactNode;
  }) => (
    <div>
      <label className='block text-sm font-medium text-slate-700 mb-1'>
        {label} {required && <span className='text-red-500'>*</span>}
      </label>
      {children}
      {error && <p className='text-red-500 text-xs mt-1'>{error}</p>}
    </div>
  )
);

// Memoized text input component
const TextInput = memo(
  ({
    field,
    type = 'text',
    disabled,
    error,
    numericOnly,
    ...props
  }: {
    field: {
      name: string;
      value: string | number | null | undefined;
      onChange: (event: any) => void;
      onBlur: () => void;
    };
    type?: string;
    disabled?: boolean;
    error?: string;
    numericOnly?: boolean;
    [key: string]: unknown;
  }) => {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!numericOnly) return;

      const allowedKeys = [
        'Backspace',
        'Tab',
        'ArrowLeft',
        'ArrowRight',
        'Delete',
        'Home',
        'End'
      ];

      const isNumberKey = e.key >= '0' && e.key <= '9';
      const isDot = e.key === '.';

      if (isDot && e.currentTarget.value.includes('.')) {
        e.preventDefault();
        return;
      }

      if (!allowedKeys.includes(e.key) && !isNumberKey && !isDot) {
        e.preventDefault();
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (type === 'number') {
        const raw = e.target.value;
        const value =
          raw === '' || raw === null || Number.isNaN(Number(raw))
            ? null
            : Number(raw);
        field.onChange(value);
      } else {
        field.onChange(e);
      }
    };

    return (
      <input
        name={field.name}
        value={field.value ?? ''}
        onChange={handleChange}
        onBlur={field.onBlur}
        onKeyDown={handleKeyDown}
        {...props}
        type={type}
        className={`border rounded px-2 py-1.5 w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
          error ? 'border-red-500' : 'border-gray-300'
        }`}
        disabled={disabled}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={error ? `${field.name}-error` : undefined}
      />
    );
  }
);

// Memoized loading indicator
const LoadingIndicator = memo(() => (
  <div className='mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg'>
    <div className='flex items-center gap-2'>
      <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600' />
      <span className='text-blue-600'>Loading staff details...</span>
    </div>
  </div>
));

// Memoized form actions
const FormActions = memo(
  ({
    isSubmitting,
    isPending,
    isFetching,
    isEdit,
    onCancel,
    onReset
  }: {
    isSubmitting: boolean;
    isPending: boolean;
    isFetching: boolean;
    isEdit: boolean;
    onCancel: () => void;
    onReset: () => void;
  }) => {
    const isDisabled = isSubmitting || isPending || isFetching;

    return (
      <div className='flex gap-4 mt-4'>
        <button
          type='submit'
          disabled={isDisabled}
          className={`bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            isDisabled ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          aria-label={isEdit ? 'Update staff' : 'Save staff'}
        >
          {isSubmitting || isPending
            ? isEdit
              ? 'Updating...'
              : 'Saving...'
            : isEdit
              ? 'Update'
              : 'Save'}
        </button>
        <button
          type='button'
          disabled={isDisabled}
          onClick={onCancel}
          className='bg-gray-200 text-slate-700 px-6 py-2 rounded hover:bg-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500'
          aria-label='Cancel and return to staff list'
        >
          Cancel
        </button>
        {!isEdit && (
          <button
            type='button'
            disabled={isDisabled}
            onClick={onReset}
            className='bg-gray-200 text-slate-700 px-6 py-2 rounded hover:bg-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500'
            aria-label='Reset form to default values'
          >
            Reset
          </button>
        )}
      </div>
    );
  }
);

const AddStaffManager: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [isPending, startTransition] = useTransition();

  const { data: staffDetails, isFetching } = useGetStaffDetailsQuery(
    id ? Number(id) : 0
  );
  const {
    data: { items: prefectureList = [] } = {} as PrefectureListResponse, // Type assertion for data
    isFetching: isFetchingPrefecture
  } = useGetPrefectureListQuery('region_id=all') as {
    data?: PrefectureListResponse;
    isFetching: boolean;
  };

  const prefectureListOptions = useMemo(() => {
    return (prefectureList as Prefecture[]).map((prefecture: Prefecture) => ({
      value: prefecture.name,
      label: prefecture.name
    }));
  }, [prefectureList]);

  const { mutateAsync: createNewStaffMutation } = useCreateNewStaffMutation();
  const { mutateAsync: editStaffMutation } = useEditStaffMutation();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    trigger
  } = useStaffForm(id, staffDetails);

  // Memoized form submission handler
  const handleFormSubmit = useCallback(
    async (data: StaffManagerFormData) => {
      const isValid = await trigger();
      if (!isValid) {
        toast.error('Please fill in all required fields correctly');
        return;
      }

      startTransition(async () => {
        try {
          // Create the exact payload structure as specified
          const submitData = {
            name: data.name.trim(),
            dob: data.dob,
            email: data.email.trim(),
            phone: data.phone.trim(),
            address_line_1: data.address_line_1.trim(),
            address_line_2: data.address_line_2.trim(),
            city: data.city.trim(),
            prefecture: data.prefecture.trim(),
            zip_code: data.zip_code.trim(),
            my_number: data.my_number.trim(),
            status: data.status,
            pay_period: data.pay_period,
            // always send pay_period_month as the first day of that month (YYYY-MM-01)
            pay_period_month: data.pay_period_month
              ? `${data.pay_period_month}-01`
              : '',
            payment_date: Number(data.payment_date),
            monthly_pay_rate: data.monthly_pay_rate
              ? Number(data.monthly_pay_rate)
              : null,
            federal_tax: data.federal_tax ? Number(data.federal_tax) : null,
            health_insurance: data.health_insurance
              ? Number(data.health_insurance)
              : null,
            pension: data.pension ? Number(data.pension) : null,
            insurance: data.insurance ? Number(data.insurance) : null
          };

          let response;
          if (id) {
            response = await editStaffMutation({
              id: Number(id),
              ...submitData
            });
          } else {
            response = await createNewStaffMutation(submitData);
          }

          if (response?.success) {
            toast.success(
              id ? 'Staff updated successfully' : 'Staff created successfully'
            );
            navigate(ROUTE_PATHS.STAFF_MANAGER_LIST);
          }
        } catch {
          toast.error('An unexpected error occurred');
        }
      });
    },
    [createNewStaffMutation, editStaffMutation, id, navigate, trigger]
  );

  // Memoized event handlers
  const handleReset = useCallback(() => {
    reset();
  }, [reset]);

  const handleCancel = useCallback(() => {
    navigate(ROUTE_PATHS.STAFF_MANAGER_LIST);
  }, [navigate]);

  const isEdit = Boolean(id);

  return (
    <div className='p-8 bg-gradient-to-br from-slate-50 to-white'>
      {isFetching && isEdit && <LoadingIndicator />}

      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className='space-y-8'
        noValidate
      >
        {/* Personal Information */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
          <FormField label='Name' required error={errors.name?.message}>
            <Controller
              name='name'
              control={control}
              render={({ field }) => (
                <TextInput
                  field={field}
                  placeholder='Enter Name'
                  disabled={isSubmitting || isPending || isFetching}
                  error={errors.name?.message}
                />
              )}
            />
          </FormField>

          <FormField label='Date of Birth' required error={errors.dob?.message}>
            <Controller
              name='dob'
              control={control}
              render={({ field }) => (
                <TextInput
                  field={field}
                  type='date'
                  disabled={isSubmitting || isPending || isFetching}
                  error={errors.dob?.message}
                />
              )}
            />
          </FormField>

          <FormField label='Email' required error={errors.email?.message}>
            <Controller
              name='email'
              control={control}
              render={({ field }) => (
                <TextInput
                  field={field}
                  type='email'
                  placeholder='Enter Email'
                  disabled={isSubmitting || isPending || isFetching}
                  error={errors.email?.message}
                />
              )}
            />
          </FormField>
        </div>

        {/* Contact Information */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
          <FormField label='Phone' required error={errors.phone?.message}>
            <Controller
              name='phone'
              control={control}
              render={({ field }) => (
                <TextInput
                  field={field}
                  numericOnly
                  type='tel'
                  placeholder='Enter Phone'
                  disabled={isSubmitting || isPending || isFetching}
                  error={errors.phone?.message}
                />
              )}
            />
          </FormField>

          <FormField
            label='Address Line 1'
            required
            error={errors.address_line_1?.message}
          >
            <Controller
              name='address_line_1'
              control={control}
              render={({ field }) => (
                <TextInput
                  field={field}
                  placeholder='Enter Address Line 1'
                  disabled={isSubmitting || isPending || isFetching}
                  error={errors.address_line_1?.message}
                />
              )}
            />
          </FormField>

          <FormField
            label='Address Line 2'
            required
            error={errors.address_line_2?.message}
          >
            <Controller
              name='address_line_2'
              control={control}
              render={({ field }) => (
                <TextInput
                  field={field}
                  placeholder='Enter Address Line 2'
                  disabled={isSubmitting || isPending || isFetching}
                  error={errors.address_line_2?.message}
                />
              )}
            />
          </FormField>
        </div>

        {/* Address Information */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
          <FormField label='City/Town' required error={errors.city?.message}>
            <Controller
              name='city'
              control={control}
              render={({ field }) => (
                <TextInput
                  field={field}
                  placeholder='Enter City/Town'
                  disabled={isSubmitting || isPending || isFetching}
                  error={errors.city?.message}
                />
              )}
            />
          </FormField>

          <FormSelect
            name='prefecture'
            control={control}
            label='Prefecture'
            required
            options={prefectureListOptions || []}
            placeholder='Select Prefecture'
            disabled={
              isFetchingPrefecture || isSubmitting || isPending || isFetching
            }
            searchable
          />

          <FormField label='Zip Code' required error={errors.zip_code?.message}>
            <Controller
              name='zip_code'
              control={control}
              render={({ field }) => (
                <TextInput
                  field={field}
                  numericOnly
                  placeholder='Enter Zip Code'
                  disabled={isSubmitting || isPending || isFetching}
                  error={errors.zip_code?.message}
                />
              )}
            />
          </FormField>
        </div>

        {/* Employment Information */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
          <FormField
            label='My Number'
            required
            error={errors.my_number?.message}
          >
            <Controller
              name='my_number'
              control={control}
              render={({ field }) => (
                <TextInput
                  field={field}
                  numericOnly
                  placeholder='Enter My Number'
                  disabled={isSubmitting || isPending || isFetching}
                  error={errors.my_number?.message}
                />
              )}
            />
          </FormField>

          <FormSelect
            name='status'
            control={control}
            label='Status'
            required
            options={NUMERIC_ACTIVE_INACTIVE_OPTIONS || []}
            placeholder='Select Status'
            disabled={isSubmitting || isPending || isFetching}
          />

          <FormSelect
            name='pay_period'
            control={control}
            label='Pay Period'
            required
            options={PAY_PERIOD_OPTIONS || []}
            placeholder='Select Pay Period'
            disabled={isSubmitting || isPending || isFetching}
          />
        </div>

        {/* Compensation Details */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
          <FormSelect
            name='payment_date'
            control={control}
            label='Payment Date'
            required
            options={CreateArrayOptions(31, 'Select Date')}
            placeholder='Select Payment Date'
            disabled={isSubmitting || isPending || isFetching}
          />

          <FormField
            label='Monthly Pay Rate'
            required
            error={errors.monthly_pay_rate?.message}
          >
            <Controller
              name='monthly_pay_rate'
              control={control}
              render={({ field }) => (
                <TextInput
                  field={field}
                  type='text'
                  placeholder='Enter Monthly Pay Rate'
                  numericOnly
                  disabled={isSubmitting || isPending || isFetching}
                  error={errors.monthly_pay_rate?.message}
                />
              )}
            />
          </FormField>

          <FormField
            label='Pay Month'
            required
            error={errors.pay_period_month?.message}
          >
            <Controller
              name='pay_period_month'
              control={control}
              render={({ field }) => (
                <input
                  type='month'
                  value={(field.value as string) || ''}
                  onChange={e => {
                    field.onChange(e.target.value);
                  }}
                  className='border border-gray-300 rounded-sm px-2 py-1.5 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full'
                />
              )}
            />
          </FormField>
        </div>

        {/* Deductions */}
        <div className='grid grid-cols-1 md:grid-cols-4 gap-6'>
          <FormField
            label='Federal Tax'
            required
            error={errors.federal_tax?.message}
          >
            <Controller
              name='federal_tax'
              control={control}
              render={({ field }) => (
                <TextInput
                  field={field}
                  type='text'
                  placeholder='Enter Federal Tax'
                  numericOnly
                  disabled={isSubmitting || isPending || isFetching}
                  error={errors.federal_tax?.message}
                />
              )}
            />
          </FormField>

          <FormField
            label='Health Insurance'
            required
            error={errors.health_insurance?.message}
          >
            <Controller
              name='health_insurance'
              control={control}
              render={({ field }) => (
                <TextInput
                  field={field}
                  type='text'
                  placeholder='Enter Health Insurance'
                  numericOnly
                  disabled={isSubmitting || isPending || isFetching}
                  error={errors.health_insurance?.message}
                />
              )}
            />
          </FormField>

          <FormField label='Pension' required error={errors.pension?.message}>
            <Controller
              name='pension'
              control={control}
              render={({ field }) => (
                <TextInput
                  field={field}
                  type='text'
                  placeholder='Enter Pension'
                  numericOnly
                  disabled={isSubmitting || isPending || isFetching}
                  error={errors.pension?.message}
                />
              )}
            />
          </FormField>

          <FormField
            label='Insurance'
            required
            error={errors.insurance?.message}
          >
            <Controller
              name='insurance'
              control={control}
              render={({ field }) => (
                <TextInput
                  field={field}
                  type='text'
                  placeholder='Enter Insurance'
                  numericOnly
                  disabled={isSubmitting || isPending || isFetching}
                  error={errors.insurance?.message}
                />
              )}
            />
          </FormField>
        </div>

        <FormActions
          isSubmitting={isSubmitting}
          isPending={isPending}
          isFetching={isFetching}
          isEdit={isEdit}
          onCancel={handleCancel}
          onReset={handleReset}
        />
      </form>
    </div>
  );
};

export default AddStaffManager;
